import React, { useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react';
import { Badge, Button, Input } from '../../components/core';

interface DesignSystemProps {
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const DesignSystem: React.FC<DesignSystemProps> = ({ addToast }) => {
  const [activeTab, setActiveTab] = useState<'tokens' | 'buttons' | 'badges' | 'toasts'>('tokens');
  const [demoInput, setDemoInput] = useState('');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-lg md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-black tracking-tight text-white">
            Admin Design System & Component Playground
          </h1>
          <p className="mt-1 text-xs text-slate-400">
            Design tokens, color swatches, button states, and notification toasts sandbox.
          </p>
        </div>

        <div className="flex items-center rounded-xl border border-slate-800 bg-slate-950 p-1">
          {(
            [
              ['tokens', 'Tokens & Colors'],
              ['buttons', 'Buttons & Inputs'],
              ['badges', 'Badges & Statuses'],
              ['toasts', 'Toast Sandbox'],
            ] as const
          ).map(([tab, label]) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`cursor-pointer rounded-lg px-3.5 py-1.5 text-xs font-bold transition-all ${
                activeTab === tab ? 'admin-tab-active' : 'admin-tab-idle text-slate-400 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'tokens' && (
        <div className="space-y-6">
          <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-md">
            <h3 className="text-sm font-extrabold text-white">Color Palette Swatches</h3>

            <div className="grid grid-cols-2 gap-4 font-mono text-xs sm:grid-cols-4 lg:grid-cols-6">
              <div className="space-y-1">
                <div className="flex h-16 items-center justify-center rounded-xl border border-slate-800 bg-[var(--gv-brand-dark)] text-white">
                  Brand
                </div>
                <div className="font-bold text-white">--gv-brand</div>
                <div className="text-[10px] text-slate-500">Primary Brand</div>
              </div>

              <div className="space-y-1">
                <div className="flex h-16 items-center justify-center rounded-xl border border-slate-800 bg-[var(--gv-brand-soft)] text-[var(--gv-brand)]">
                  Soft
                </div>
                <div className="font-bold text-white">--gv-brand-soft</div>
                <div className="text-[10px] text-slate-500">Soft Surface</div>
              </div>

              <div className="space-y-1">
                <div className="flex h-16 items-center justify-center rounded-xl bg-emerald-600 font-bold text-white">
                  #059669
                </div>
                <div className="font-bold text-white">Emerald 600</div>
                <div className="text-[10px] text-slate-500">Success / Approved</div>
              </div>

              <div className="space-y-1">
                <div className="flex h-16 items-center justify-center rounded-xl bg-amber-600 font-bold text-white">
                  #d97706
                </div>
                <div className="font-bold text-white">Amber 600</div>
                <div className="text-[10px] text-slate-500">Reward Points / Pending</div>
              </div>

              <div className="space-y-1">
                <div className="flex h-16 items-center justify-center rounded-xl bg-rose-600 font-bold text-white">
                  #e11d48
                </div>
                <div className="font-bold text-white">Rose 600</div>
                <div className="text-[10px] text-slate-500">Danger / Rejected</div>
              </div>

              <div className="space-y-1">
                <div className="flex h-16 items-center justify-center rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] text-[var(--gv-ink-muted)]">
                  Surface
                </div>
                <div className="font-bold text-white">--gv-surface</div>
                <div className="text-[10px] text-slate-500">Main Canvas</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'buttons' && (
        <div className="space-y-6 rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-md">
          <h3 className="text-sm font-extrabold text-white">Button Variants & Controls</h3>

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary">Primary Button</Button>
            <Button variant="secondary">Secondary Button</Button>
            <Button variant="outline">Outline Button</Button>
            <Button variant="ghost">Ghost Button</Button>
            <Button variant="danger">Danger Button</Button>
          </div>

          <div className="flex flex-wrap items-end gap-3 border-t border-slate-800 pt-4">
            <Button size="sm">Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg">Large</Button>
          </div>

          <div className="max-w-sm border-t border-slate-800 pt-4">
            <Input
              label="Core Input"
              placeholder="Type to preview admin-input styling..."
              value={demoInput}
              onChange={(e) => setDemoInput(e.target.value)}
            />
          </div>
        </div>
      )}

      {activeTab === 'badges' && (
        <div className="space-y-6 rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-md">
          <h3 className="text-sm font-extrabold text-white">Badge Pills & Indicators</h3>

          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="default">Active Session</Badge>
            <Badge variant="success">Approved</Badge>
            <Badge variant="warning">Pending Approval</Badge>
            <Badge variant="danger">Rejected / Terminated</Badge>
            <Badge variant="soft">Soft Brand</Badge>
            <Badge variant="accent">Accent</Badge>
            <Badge variant="outline">Outline</Badge>
          </div>
        </div>
      )}

      {activeTab === 'toasts' && (
        <div className="space-y-6 rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-md">
          <h3 className="text-sm font-extrabold text-white">Toast Notification Dispatcher Sandbox</h3>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Button
              variant="outline"
              className="h-auto flex-col gap-1 border-emerald-800/80 bg-emerald-950/60 p-4 text-emerald-200 hover:bg-emerald-900"
              onClick={() => addToast('success', 'Operation Completed', 'Student enrollment approved successfully.')}
            >
              <CheckCircle2 className="mx-auto h-5 w-5 text-emerald-400" />
              Trigger Success Toast
            </Button>

            <Button
              variant="outline"
              className="h-auto flex-col gap-1 border-rose-800/80 bg-rose-950/60 p-4 text-rose-200 hover:bg-rose-900"
              onClick={() => addToast('error', 'Authentication Error', 'Invalid portal access key or credentials.')}
            >
              <XCircle className="mx-auto h-5 w-5 text-rose-400" />
              Trigger Error Toast
            </Button>

            <Button
              variant="outline"
              className="h-auto flex-col gap-1 border-amber-800/80 bg-amber-950/60 p-4 text-amber-200 hover:bg-amber-900"
              onClick={() =>
                addToast('warning', 'Cutoff Approaching', 'Daily Niyam submission window closes in 15 mins.')
              }
            >
              <AlertTriangle className="mx-auto h-5 w-5 text-amber-400" />
              Trigger Warning Toast
            </Button>

            <Button
              variant="outline"
              className="h-auto flex-col gap-1 border-indigo-800/80 bg-indigo-950/60 p-4 text-indigo-200 hover:bg-indigo-900"
              onClick={() => addToast('info', 'System Sync Active', 'Refreshing Pathshala database records...')}
            >
              <Info className="mx-auto h-5 w-5 text-indigo-400" />
              Trigger Info Toast
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
